<!--
  D3 知识图谱容器：内部动态插入 <svg>。
  ref="wrap" 供测量宽高、截图、清空图谱使用。
-->
<template>
    <div ref="wrap" class="d3-knowledge-graph complex-d3DIV"></div>
</template>

<script>
/**
 * D3KnowledgeGraph — 力导向知识图谱（SVG 渲染）
 *
 * 默认知识图谱样式：浅灰底、node.degree 着色、半透明弧形连线；
 * 预热布局后持续力导向动画（中心 hub 固定）。
 * preserveNodeStyle + clusterKeyFn 时保留调用方自定义配色与分簇横向布局。
 */
import * as d3 from 'd3';

/** 无 node.distance / node.degree 时的默认节点色 */
const DEFAULT_NODE_COLOR ='#47a8ff'// '#396de7';

/** distance 为有效颜色值（聚类色带等） */
function distanceColor(distance) {
    if (distance == null || distance === '' || distance === 0) return null;
    return String(distance);
}

/** 为节点生成稳定字符串 ID，供力导向 link.id 与 Map 查找 */
function nodeId(node, index) {
    if (node == null) return `__${index}`;
    if (node.id != null && node.id !== '') return String(node.id);
    if (node.name != null && node.name !== '') return String(node.name);
    return `__n${index}`;
}

/** 边端点：支持对象或字符串；对象时与 nodeId 规则一致 */
function linkEndpointKey(v, indexHint) {
    if (v == null) return null;
    if (typeof v === 'object') {
        return nodeId(v, indexHint);
    }
    return String(v);
}

/** 弧线控制点：沿连线法向偏移，seed 决定弯曲方向 */
function linkCurveControl(sx, sy, tx, ty, seed, curvature = 0.22) {
    const dx = tx - sx;
    const dy = ty - sy;
    const dr = Math.sqrt(dx * dx + dy * dy) || 1;
    const mx = (sx + tx) / 2;
    const my = (sy + ty) / 2;
    const sign = seed % 2 === 0 ? 1 : -1;
    const offset = dr * curvature * sign;
    return {
        cx: mx + (-dy / dr) * offset,
        cy: my + (dx / dr) * offset,
    };
}

/** 根据填充色亮度选择黑/白标签 */
function textColorForFill(fill) {
    if (!fill || fill.charAt(0) !== '#') return '#1a3a5c';
    const hex = fill.length === 4
        ? `#${fill[1]}${fill[1]}${fill[2]}${fill[2]}${fill[3]}${fill[3]}`
        : fill;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.62 ? '#1a3a5c' : '#ffffff';
}

/** 将整图缩放平移后居中适配视口（多节点数据） */
function fitGraphToView(nodeList, viewW, viewH, radiusFn, padding = 48, scaleMin = 0.15) {
    if (!nodeList.length) return d3.zoomIdentity;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    nodeList.forEach((d) => {
        const r = radiusFn(d);
        minX = Math.min(minX, d.x - r);
        maxX = Math.max(maxX, d.x + r);
        minY = Math.min(minY, d.y - r);
        maxY = Math.max(maxY, d.y + r);
    });
    const graphW = Math.max(maxX - minX, 1);
    const graphH = Math.max(maxY - minY, 1);
    const k = Math.min(
        (viewW - padding * 2) / graphW,
        (viewH - padding * 2) / graphH,
    );
    const safeK = Math.max(scaleMin, Math.min(k, 1));
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    return d3.zoomIdentity
        .translate(viewW / 2, viewH / 2)
        .scale(safeK)
        .translate(-cx, -cy);
}

/** 多组中心点：沿水平方向从左到右均匀分布，单组居中 */
function horizontalGroupCenters(groupIds, viewW, viewH, spreadRatio = 0.72) {
    const cx0 = viewW / 2;
    const cy0 = viewH / 2;
    const centers = {};
    const n = groupIds.length;
    if (!n) return centers;
    if (n === 1) {
        centers[groupIds[0]] = { x: cx0, y: cy0 };
        return centers;
    }
    const span = Math.max(viewW * spreadRatio, 160);
    const startX = cx0 - span / 2;
    const step = span / (n - 1);
    groupIds.forEach((id, i) => {
        centers[id] = {
            x: startX + step * i,
            y: cy0,
        };
    });
    return centers;
}

/** 无向图连通分量（返回各分量内的节点 _nid 列表） */
function findConnectedComponents(nodes, adjacency) {
    const visited = new Set();
    const components = [];
    nodes.forEach((node) => {
        const startId = node._nid;
        if (visited.has(startId)) return;
        const stack = [startId];
        const component = [];
        visited.add(startId);
        while (stack.length) {
            const id = stack.pop();
            component.push(id);
            const neighbors = adjacency.get(id);
            if (!neighbors) continue;
            neighbors.forEach((nid) => {
                if (!visited.has(nid)) {
                    visited.add(nid);
                    stack.push(nid);
                }
            });
        }
        components.push(component);
    });
    return components;
}

export default {
    name: 'D3KnowledgeGraph',
    props: {
        /** 图数据：nodes（含 degree、name/label）、edges（source/target 或 startNode/endNode） */
        graphData: {
            type: Object,
            default() {
                return { nodes: [], edges: [] };
            },
        },
        /** hidden：边标签默认透明，悬停显示；show：常显半透明 */
        sideLabel: {
            type: String,
            default: 'hidden',
        },
        /**
         * 悬停节点时，“无关元素”的透明度（0~1）
         * - 1：不变淡
         * - 0：完全不可见
         */
        hoverDimOpacity: {
            type: Number,
            default: 0.15,
        },
        /** true：不覆盖节点颜色，沿用 graphData 中 style（计量分析等） */
        preserveNodeStyle: {
            type: Boolean,
            default: false,
        },
        /** 自定义分簇键；不传则按 node.degree 字符串分簇（知识图谱） */
        clusterKeyFn: {
            type: Function,
            default: null,
        },
        /** true：半径用节点 _r（或 size/2），否则按连接度缩放 */
        variableRadius: {
            type: Boolean,
            default: true,
        },
        linkDistance: {
            type: Number,
            default: 88,
        },
        layoutTicks: {
            type: Number,
            default: 480,
        },
        layoutCharge: {
            type: Number,
            default: -320,
        },
        layoutClusterStrength: {
            type: Number,
            default: 0.38,
        },
        /** 多组横向间距 = min(w,h) * 该比例（兼容原 ringRadiusRatio） */
        ringRadiusRatio: {
            type: Number,
            default: 0.3,
        },
        /** 初始随机偏移（像素） */
        initialJitter: {
            type: Number,
            default: 60,
        },
        /** >0 时节点文字超长截断并加 … */
        nodeLabelMaxLen: {
            type: Number,
            default: 0,
        },
        /** d3.zoom 缩放范围 [min, max] */
        zoomScaleExtent: {
            type: Array,
            default() {
                return [0.15, 5];
            },
        },
    },
    data() {
        return {
            graphWidth: 0, // 容器内容宽度（px）
            graphHeight: 0, // 容器内容高度（px）
            _d3Simulation: null, // 力模拟引用（tick 结束后会置空）
            _graphTeardown: null, // 卸载 svg 事件与 zoom 的清理函数
        };
    },
    watch: {
        /** 数据或边标签策略变化时重绘整张图 */
        graphData: {
            handler() {
                this.$nextTick(() => this.renderGraph());
            },
            deep: true,
        },
        sideLabel() {
            this.$nextTick(() => this.renderGraph());
        },
        /** 布局参数、悬停变淡、缩放范围等变化时重绘 */
        layoutSignature() {
            this.$nextTick(() => this.renderGraph());
        },
    },
    computed: {
        /** 供 watch：任一布局相关 prop 变化则重算图谱 */
        layoutSignature() {
            const z = this.zoomScaleExtent || [0.15, 5];
            return [
                this.preserveNodeStyle,
                this.variableRadius,
                this.linkDistance,
                this.layoutTicks,
                this.layoutCharge,
                this.layoutClusterStrength,
                this.ringRadiusRatio,
                this.nodeLabelMaxLen,
                this.initialJitter,
                this.hoverDimOpacity,
                z[0],
                z[1],
                this.clusterKeyFn ? 1 : 0,
            ].join('|');
        },
    },
    mounted() {
        // 首次挂载后按容器尺寸渲染
        this.$nextTick(() => this.renderGraph());
    },
    beforeDestroy() {
        // 移除监听、清空 DOM，避免泄漏
        this.destroyD3Graph();
    },
    methods: {
        /**
         * 销毁当前图谱与交互；父组件在截图前若需保证状态一致可主动调用
         */
        destroyD3Graph() {
            if (typeof this._graphTeardown === 'function') {
                this._graphTeardown();
                this._graphTeardown = null;
            }
            if (this._d3Simulation) {
                this._d3Simulation.stop();
                this._d3Simulation = null;
            }
            const el = this.$refs.wrap;
            if (el) {
                el.innerHTML = '';
            }
        },
        /**
         * 边标签透明度：与 sideLabel 联动
         * hidden — 默认 0，悬停相关边时为 1
         * show — 默认 0.5，悬停时 1
         */
        edgeLabelOpacity(num, type) {
            let opacity = num;
            if (this.sideLabel === 'show') {
                opacity = 0.5;
                if (type === 'hover') {
                    opacity = 1;
                }
            } else {
                opacity = num;
            }
            return opacity;
        },
        /** 知识图谱主题：半径按连接度，颜色按 node.degree */
        applyBlueTheme(nodes, linkDegreeMap) {
            const counts = nodes.map((n) => linkDegreeMap.get(n._nid) || 0);
            const minD = Math.min(...counts, 0);
            const maxD = Math.max(...counts, 1);
            const lo = minD === maxD ? Math.max(0, minD - 1) : minD;
            const hi = maxD || 1;
            const radiusScale = d3.scaleSqrt().domain([lo, hi]).range([14, 44]);
            nodes.forEach((n) => {
                const linkDeg = linkDegreeMap.get(n._nid) || 0;
                n._linkDegree = linkDeg;
                n._r = radiusScale(linkDeg);
                this.nodeFill(n);
                if (!n.style) n.style = {};
                n.style.lineWidth = 0;
            });
        },
        /** 优先 node.distance 着色；否则按 degree（业务分类）；均无则用 DEFAULT_NODE_COLOR */
        nodeFill(node) {
            const distColor = distanceColor(node.distance);
            if (distColor) {
                if (!node.style) node.style = {};
                node.style.fill = distColor;
                node.style.stroke = distColor;
                return;
            }
            const deg = node.degree;
            let color = DEFAULT_NODE_COLOR;
            if (deg == null || deg === '') {
                if (!node.style) node.style = {};
                node.style.fill = color;
                node.style.stroke = color;
                return;
            }
            if (deg === '中医治则') {
                color = '#fca3d9';
            } else if (deg === '中医治疗') {
                color = '#e84dff';
            } else if (deg === '中医疾病') {
                color = '#c147fa';
            } else if (deg === '中医证候') {
                color = '#9a80ff';
            } else if (deg === '中医诊断') {
                color = '#c0c8fd';
            } else if (deg === '中药') {
                color = '#fdcece';
            } else if (deg === '临床表现') {
                color = '#3ba7fa';
            } else if (deg === '体格检查') {
                color = '#60f6fc';
            } else if (deg === '方剂') {
                color = '#78cea3';
            } else if (deg === '治法') {
                color = '#96fe82';
            } else if (deg === '症状') {
                color = '#dafe5c';
            } else if (deg === '穴位') {
                color = '#fbbd37';
            } else if (deg === '西医治疗') {
                color = '#fd9526';
            } else if (deg === '西医疾病') {
                color = '#ff6035';
            } else if (deg === '辅助检查') {
                color = '#bffaee';
            } else {
                color = DEFAULT_NODE_COLOR;
            }
            if (!node.style) node.style = {};
            node.style.fill = color;
            node.style.stroke = color;
        },
        /**
         * 入口：清空旧图 → 读容器尺寸 → 无节点则退出
         */
        renderGraph() {
            this.destroyD3Graph();
            const nodesIn = (this.graphData && this.graphData.nodes) || [];
            if (!nodesIn.length) return;

            const wrap = this.$refs.wrap;
            if (!wrap) return;
            // console.log(this.graphWidth,'=this.graphWidth')
            this.graphWidth = wrap.clientWidth || 1198;
            this.graphHeight = wrap.clientHeight || 700;

            this.graphTab();
        },
        /**
         * 核心：准备数据 → 力导向布局 → SVG 绘制 → 绑定缩放/拖拽/悬停
         */
        graphTab() {
            const container = this.$refs.wrap;
            if (!container) return;

            const width = this.graphWidth;
            const height = this.graphHeight;
            const vm = this;

            const nodesIn = (this.graphData && this.graphData.nodes) || [];
            const edgesIn = (this.graphData && this.graphData.edges) || [];

            const useBlueTheme = !vm.preserveNodeStyle;
            const useClusterLayout = Boolean(vm.clusterKeyFn);

            const nodeRadius = (d) => {
                if (d._r != null && d._r > 0) {
                    return d._r;
                }
                if (vm.variableRadius) {
                    return Math.max(4, (d.size || 10) / 2);
                }
                return useBlueTheme ? 18 : 20;
            };

            // ---------- 1. 节点：拷贝 + 着色 + _nid；边：解析端点并过滤无效边 ----------
            const clusterKey = (d) => {
                if (vm.clusterKeyFn) return vm.clusterKeyFn(d);
                const k = d.degree;
                return k != null && k !== '' ? String(k) : '其他';
            };

            const rawNodes = nodesIn.map((n, i) => {
                const o = Object.assign({}, n);
                if (!o.style) o.style = {};
                if (vm.preserveNodeStyle) {
                    const distColor = distanceColor(o.distance);
                    if (distColor) {
                        o.style.fill = distColor;
                        o.style.stroke = distColor;
                    } else if (!o.style.fill) {
                        o.style.fill = '#3ba7fa';
                        o.style.stroke = o.style.fill;
                    }
                }
                if (vm.preserveNodeStyle && vm.variableRadius && (o._r == null || o._r <= 0)) {
                    o._r = Math.max(4, (o.size || 10) / 2);
                }
                o._nid = nodeId(o, i);
                return o;
            });
            const nodeById = new Map(rawNodes.map((n) => [n._nid, n]));

            const rawLinks = edgesIn.map((e, i) => ({
                ...e,
                _sourceId: linkEndpointKey(e.source != null ? e.source : e.startNode, i),
                _targetId: linkEndpointKey(e.target != null ? e.target : e.endNode, i),
            })).filter(
                (l) =>
                    l._sourceId &&
                    l._targetId &&
                    nodeById.has(l._sourceId) &&
                    nodeById.has(l._targetId)
            );

            const links = rawLinks.map((l, i) => ({
                source: l._sourceId,
                target: l._targetId,
                relation: l.relation,
                label: l.label,
                _curveSeed: i,
            }));

            const linkDegreeMap = new Map();
            rawNodes.forEach((n) => linkDegreeMap.set(n._nid, 0));
            rawLinks.forEach((l) => {
                linkDegreeMap.set(l._sourceId, (linkDegreeMap.get(l._sourceId) || 0) + 1);
                linkDegreeMap.set(l._targetId, (linkDegreeMap.get(l._targetId) || 0) + 1);
            });

            const nodes = rawNodes.map((n) => Object.assign({}, n));
            if (useBlueTheme) {
                vm.applyBlueTheme(nodes, linkDegreeMap);
            } else {
                nodes.forEach((n) => vm.nodeFill(n));
            }

            // 邻接表：用于 hoverNode 时判断“相关/无关”
            const adjacency = new Map();
            const addAdj = (a, b) => {
                if (!adjacency.has(a)) adjacency.set(a, new Set());
                adjacency.get(a).add(b);
            };
            rawLinks.forEach((l) => {
                addAdj(l._sourceId, l._targetId);
                addAdj(l._targetId, l._sourceId);
            });

            const cx0 = width / 2;
            const cy0 = height / 2;

            // ---------- 2. 初始位置：多组横向布局 或 中心力导向 ----------
            const clusterCenters = {};
            const jitter = vm.initialJitter;
            let layoutGroupKey = null;
            let layoutGroupIds = [];

            const clusterIds = useClusterLayout
                ? [...new Set(nodes.map(clusterKey))].sort()
                : [];
            const components = findConnectedComponents(nodes, adjacency);
            const nodeToComp = new Map();
            const componentIds = components.map((comp, i) => {
                const id = `__comp${i}`;
                comp.forEach((nid) => nodeToComp.set(nid, id));
                return id;
            });

            if (useClusterLayout && clusterIds.length > 1) {
                layoutGroupIds = clusterIds;
                layoutGroupKey = clusterKey;
            } else if (components.length > 1) {
                layoutGroupIds = componentIds;
                layoutGroupKey = (d) => nodeToComp.get(d._nid);
            } else if (useClusterLayout) {
                layoutGroupIds = clusterIds;
                layoutGroupKey = clusterKey;
            }

            const hasLayoutGroups = Boolean(layoutGroupKey);
            if (hasLayoutGroups) {
                Object.assign(
                    clusterCenters,
                    horizontalGroupCenters(layoutGroupIds, width, height, vm.ringRadiusRatio),
                );
                nodes.forEach((n) => {
                    const c = clusterCenters[layoutGroupKey(n)] || { x: cx0, y: cy0 };
                    n.x = c.x + (Math.random() - 0.5) * jitter;
                    n.y = c.y + (Math.random() - 0.5) * jitter;
                });
            } else {
                nodes.forEach((n) => {
                    n.x = cx0 + (Math.random() - 0.5) * jitter * 2;
                    n.y = cy0 + (Math.random() - 0.5) * jitter * 2;
                });
            }

            let hubNode = null;
            if (useBlueTheme && nodes.length) {
                hubNode = nodes.reduce((a, b) => (
                    (linkDegreeMap.get(a._nid) || 0) >= (linkDegreeMap.get(b._nid) || 0) ? a : b
                ));
                hubNode.x = cx0;
                hubNode.y = cy0;
                hubNode.fx = cx0;
                hubNode.fy = cy0;
            }

            // ---------- 3. 力导向：先预热收敛，再恢复持续 tick 动画 ----------
            const str = hasLayoutGroups ? vm.layoutClusterStrength : 0;
            const centerStrength = hasLayoutGroups
                ? 0.04
                : Math.min(0.35, 0.1 + nodes.length * 0.004);
            const simulation = d3.forceSimulation(nodes)
                .force('link', d3.forceLink(links).id((d) => d._nid).distance((l) => {
                    const sa = typeof l.source === 'object' ? l.source : nodeById.get(l.source);
                    const ta = typeof l.target === 'object' ? l.target : nodeById.get(l.target);
                    const r1 = sa ? nodeRadius(sa) : 18;
                    const r2 = ta ? nodeRadius(ta) : 18;
                    return vm.linkDistance + r1 + r2;
                }))
                .force('charge', d3.forceManyBody().strength(vm.layoutCharge))
                .force('center', d3.forceCenter(cx0, cy0).strength(centerStrength))
                .force('collision', d3.forceCollide().radius((d) => nodeRadius(d) + 3));
            if (hasLayoutGroups) {
                simulation
                    .force('x', d3.forceX((d) => (clusterCenters[layoutGroupKey(d)] || { x: cx0 }).x).strength(str))
                    .force('y', d3.forceY((d) => (clusterCenters[layoutGroupKey(d)] || { y: cy0 }).y).strength(str));
            }

            this._d3Simulation = simulation;
            simulation.stop();
            const LAYOUT_TICKS = vm.layoutTicks;
            for (let i = 0; i < LAYOUT_TICKS; i += 1) {
                simulation.tick();
            }
            if (hasLayoutGroups) {
                const liveStr = Math.min(str, 0.1);
                simulation.force('x').strength(liveStr);
                simulation.force('y').strength(liveStr);
            }

            const releaseNodesForAnimation = () => {
                nodes.forEach((d) => {
                    if (hubNode && d === hubNode) {
                        d.fx = cx0;
                        d.fy = cy0;
                    } else {
                        d.fx = null;
                        d.fy = null;
                    }
                });
            };

            // ---------- 4. SVG：根节点 + zoom 变换组 ----------
            const bgColor = useBlueTheme ? '#e8e8e8' : '#ffffff';

            const svg = d3.select(container)
                .append('svg')
                .attr('class', 'atlas-graph-svg')
                .attr('width', width)
                .attr('height', height)
                .style('display', 'block')
                .style('cursor', 'default');

            svg.append('rect')
                .attr('class', 'atlas-graph-bg')
                .attr('width', width)
                .attr('height', height)
                .attr('fill', bgColor);

            const rootG = svg.append('g');

            const linkG = rootG.append('g').attr('class', 'graph-links');
            const linkLabelG = rootG.append('g').attr('class', 'graph-link-labels');
            const nodeG = rootG.append('g').attr('class', 'graph-nodes');

            let hoverNode = null;
            let hoverEdge = null;
            let dragNode = null;

            const getLinkCurve = (ld) => {
                const s = ld.source;
                const t = ld.target;
                const seed = ld._curveSeed != null
                    ? ld._curveSeed
                    : `${s._nid}-${t._nid}`.length;
                return linkCurveControl(s.x, s.y, t.x, t.y, seed);
            };

            const linkPathD = (ld) => {
                const s = ld.source;
                const t = ld.target;
                const { cx, cy } = getLinkCurve(ld);
                return `M${s.x},${s.y} Q${cx},${cy} ${t.x},${t.y}`;
            };

            const defaultLinkColor = useBlueTheme ? 'rgba(100, 150, 230, 0.38)' : 'rgba(98, 97, 97, 0.5)';
            const hoverLinkColor = useBlueTheme ? 'rgba(60, 110, 200, 0.75)' : '#626161';

            const linkStroke = (ld) => {
                if (hoverEdge === ld) {
                    return { stroke: hoverLinkColor, lineW: useBlueTheme ? 2 : 8, alpha: 1 };
                }
                if (hoverNode) {
                    const on = ld.source === hoverNode || ld.target === hoverNode;
                    if (on) {
                        return { stroke: hoverLinkColor, lineW: useBlueTheme ? 1.2 : 1.5, alpha: useBlueTheme ? 0.65 : 0.5 };
                    }
                    return {
                        stroke: defaultLinkColor,
                        lineW: 1,
                        alpha: Math.max(0, Math.min(1, vm.hoverDimOpacity)),
                    };
                }
                return { stroke: defaultLinkColor, lineW: 1, alpha: 1 };
            };

            const linkLabelOpacity = (ld) => {
                if (hoverEdge === ld) {
                    return vm.edgeLabelOpacity(1, 'hover');
                }
                if (hoverNode) {
                    const on = ld.source === hoverNode || ld.target === hoverNode;
                    return on
                        ? vm.edgeLabelOpacity(1, 'hover')
                        : vm.edgeLabelOpacity(0, 'hover');
                }
                return vm.edgeLabelOpacity(0);
            };

            const nodeLabelColor = (d) => {
                if (!useBlueTheme) return '#000';
                const fill = (d.style && d.style.fill) || '#5a9fd4';
                return textColorForFill(fill);
            };

            const formatNodeLabel = (d) => {
                let label = d.label || d.name || '';
                if (!label) return '';
                const r = nodeRadius(d);
                const maxLen = vm.nodeLabelMaxLen;
                if (maxLen > 0 && label.length > maxLen) {
                    return `${label.slice(0, maxLen - 1)}…`;
                }
                const fontSize = Math.max(10, Math.min(14, Math.round(r * 0.38)));
                const maxChars = Math.max(2, Math.floor((r * 1.6) / fontSize));
                if (maxLen <= 0 && label.length > maxChars) {
                    return `${label.slice(0, Math.max(1, maxChars - 1))}…`;
                }
                return label;
            };

            const linkPaths = linkG.selectAll('path.graph-link')
                .data(links)
                .join('path')
                .attr('class', 'graph-link')
                .attr('fill', 'none')
                .style('pointer-events', 'stroke');

            const linkHitPaths = linkG.selectAll('path.graph-link-hit')
                .data(links)
                .join('path')
                .attr('class', 'graph-link-hit')
                .attr('fill', 'none')
                .attr('stroke', 'transparent')
                .attr('stroke-width', 14)
                .style('pointer-events', 'stroke')
                .style('cursor', 'pointer');

            const linkLabels = linkLabelG.selectAll('text.graph-link-label')
                .data(links)
                .join('text')
                .attr('class', 'graph-link-label')
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'middle')
                .attr('font-size', '12px')
                .attr('fill', '#333')
                .text((ld) => ld.relation || ld.label || '');

            const nodeGroups = nodeG.selectAll('g.graph-node')
                .data(nodes, (d) => d._nid)
                .join('g')
                .attr('class', 'graph-node')
                .style('cursor', 'grab');

            nodeGroups.append('circle')
                .attr('class', 'graph-node-circle');

            nodeGroups.append('text')
                .attr('class', 'graph-node-label')
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'middle');

            const updatePositions = () => {
                linkPaths.attr('d', linkPathD);
                linkHitPaths.attr('d', linkPathD);
                linkLabels
                    .attr('x', (ld) => (ld.source.x + ld.target.x) / 2)
                    .attr('y', (ld) => (ld.source.y + ld.target.y) / 2);
                nodeGroups.attr('transform', (d) => `translate(${d.x},${d.y})`);
            };

            const updateStyles = () => {
                const dimAlpha = Math.max(0, Math.min(1, vm.hoverDimOpacity));
                const hoverId = hoverNode ? hoverNode._nid : null;
                const relatedSet = hoverId ? adjacency.get(hoverId) : null;
                const isRelatedNode = (d) => {
                    if (!hoverId) return true;
                    if (d._nid === hoverId) return true;
                    return relatedSet ? relatedSet.has(d._nid) : false;
                };

                linkPaths.each(function updateLinkStroke(ld) {
                    const st = linkStroke(ld);
                    d3.select(this)
                        .attr('stroke', st.stroke)
                        .attr('stroke-width', st.lineW)
                        .attr('stroke-opacity', st.alpha);
                });

                linkLabels.attr('opacity', (ld) => linkLabelOpacity(ld));

                nodeGroups.each(function updateNodeStyle(d) {
                    const g = d3.select(this);
                    const fill = (d.style && d.style.fill) || DEFAULT_NODE_COLOR;
                    const stroke = (d.style && d.style.stroke) || fill;
                    const nodeAlpha = hoverId
                        ? isRelatedNode(d)
                            ? 1
                            : dimAlpha
                        : 1;
                    const r = nodeRadius(d);
                    const lineW = (d.style && d.style.lineWidth) != null
                        ? d.style.lineWidth
                        : (vm.preserveNodeStyle ? 2 : 0);

                    g.attr('opacity', nodeAlpha);
                    g.select('.graph-node-circle')
                        .attr('r', r)
                        .attr('fill', fill)
                        .attr('stroke', stroke)
                        .attr('stroke-width', lineW);

                    const label = formatNodeLabel(d);
                    g.select('.graph-node-label')
                        .text(label)
                        .attr('font-family', vm.preserveNodeStyle
                            ? 'sans-serif'
                            : '"PingFang SC", "Microsoft YaHei", sans-serif')
                        .attr('font-size', vm.preserveNodeStyle
                            ? '12px'
                            : `${Math.max(10, Math.min(14, Math.round(r * 0.38)))}px`)
                        .attr('fill', nodeLabelColor(d))
                        .attr('opacity', label ? 1 : 0);
                });
            };

            const refresh = () => {
                updatePositions();
                updateStyles();
            };

            const pointerToGraph = (event) => {
                const t = d3.zoomTransform(svg.node());
                const [x, y] = d3.pointer(event, svg.node());
                return t.invert([x, y]);
            };

            const hitNode = (gx, gy) => {
                for (let i = nodes.length - 1; i >= 0; i -= 1) {
                    const d = nodes[i];
                    const r = nodeRadius(d);
                    const dx = gx - d.x;
                    const dy = gy - d.y;
                    if (dx * dx + dy * dy <= r * r) {
                        return d;
                    }
                }
                return null;
            };

            const setHoverNode = (node) => {
                hoverNode = node;
                hoverEdge = null;
                svg.style('cursor', node ? 'grab' : 'default');
                updateStyles();
            };

            const setHoverEdge = (edge) => {
                hoverEdge = edge;
                hoverNode = null;
                svg.style('cursor', edge ? 'pointer' : 'default');
                updateStyles();
            };

            linkHitPaths
                .on('mouseenter', (event, ld) => {
                    if (dragNode) return;
                    setHoverEdge(ld);
                })
                .on('mouseleave', () => {
                    if (dragNode) return;
                    setHoverEdge(null);
                });

            nodeGroups
                .on('mouseenter', (event, d) => {
                    if (dragNode) return;
                    setHoverNode(d);
                })
                .on('mouseleave', () => {
                    if (dragNode) return;
                    setHoverNode(null);
                });

            const dragBehavior = d3.drag()
                .on('start', (event, d) => {
                    if (event.sourceEvent && event.sourceEvent.button !== 0) return;
                    dragNode = d;
                    hoverNode = d;
                    hoverEdge = null;
                    svg.style('cursor', 'grabbing');
                    simulation.alphaTarget(0);
                    updateStyles();
                    event.sourceEvent.stopPropagation();
                })
                .on('drag', (event, d) => {
                    d.fx = event.x;
                    d.fy = event.y;
                    d.x = event.x;
                    d.y = event.y;
                    updatePositions();
                })
                .on('end', (event, d) => {
                    const pinned = hubNode && d === hubNode;
                    if (pinned) {
                        d.fx = cx0;
                        d.fy = cy0;
                        d.x = cx0;
                        d.y = cy0;
                    } else {
                        d.fx = null;
                        d.fy = null;
                    }
                    dragNode = null;
                    svg.style('cursor', 'grab');
                    simulation.alpha(0.3).alphaTarget(0.04).restart();
                    updateStyles();
                });

            nodeGroups.call(dragBehavior);

            const onSvgMouseLeave = () => {
                if (!dragNode) {
                    hoverNode = null;
                    hoverEdge = null;
                    svg.style('cursor', 'default');
                    updateStyles();
                }
            };

            svg.on('mouseleave', onSvgMouseLeave);

            // ---------- 5. 缩放：左键在空白处拖动画布；在节点上左键留给拖拽节点 ----------
            const zoom = d3.zoom()
                .scaleExtent(vm.zoomScaleExtent || [0.15, 5])
                .filter((event) => {
                    if (event.type === 'wheel') return true;
                    if (event.type === 'mousedown' && event.button === 0) {
                        const [gx, gy] = pointerToGraph(event);
                        return !hitNode(gx, gy);
                    }
                    return !event.button;
                })
                .on('zoom', (event) => {
                    rootG.attr('transform', event.transform);
                });

            svg.call(zoom);
            const zoomMin = (vm.zoomScaleExtent || [0.15, 5])[0];
            const fitPadding = nodes.length > 30 ? 56 : 48;
            const fitTransform = fitGraphToView(
                nodes,
                width,
                height,
                nodeRadius,
                fitPadding,
                zoomMin,
            );
            svg.call(zoom.transform, fitTransform);
            rootG.attr('transform', fitTransform);
            releaseNodesForAnimation();
            simulation.on('tick', refresh);
            simulation.alpha(0.35).alphaTarget(0.04).restart();
            this._d3Simulation = simulation;
            refresh();

            /** 组件销毁或下次 renderGraph 时调用，解除监听 */
            this._graphTeardown = () => {
                simulation.on('tick', null);
                simulation.stop();
                svg.on('mouseleave', null);
                svg.on('.zoom', null);
            };
        }
    }
};
</script>

<style scoped>
/* 外层需由父级给定高度（如 atlas 里 750px），否则 min-height 兜底 */
.d3-knowledge-graph {
    width: 100%;
    height: 100%;
    min-height: 200px;
    background: #e8e8e8;
}
</style>
